/**
 * ModificationsSection — Phase 6a.22 per Spec §21.7.8.
 *
 * Container for the IFRS 16 §40-46 modifications list. Replaces the
 * disabled placeholder AccordionItem that lived in
 * ContractFormContainer through Phase 6a.8. Pattern:
 *
 *   • `useFieldArray` drives add/remove on `modifications`
 *   • Count badge in the accordion trigger
 *   • Empty-state copy when no modifications yet (encourages awareness
 *     that the section is intentional, not a missing feature)
 *   • Delete goes through a confirmation dialog (destructive action)
 *   • Each card is a sibling `<ModificationCard />`
 *
 * The section sits inside the existing `<Accordion type="multiple">` so
 * users can collapse it independently of the other optional sections.
 */

import { Plus } from "lucide-react";
import * as React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type {
  ContractFormValues,
  ModificationFormValue,
} from "../schemas/contractFormSchema";

import { ModificationCard } from "./ModificationCard";

/**
 * Default value for a freshly-added modification. `modificationType`
 * is left undefined on purpose so the Select forces the auditor to
 * choose — the conditional field set depends on this value.
 */
function defaultModification(index: number): ModificationFormValue {
  return {
    modificationId: `MOD-${String(index + 1).padStart(3, "0")}`,
    modificationDate: "",
    // RHF tolerates undefined here; the schema enforces required at
    // submit. The cast is local to the seed value.
    modificationType: undefined as unknown as ModificationFormValue["modificationType"],
    description: "",
  };
}

export function ModificationsSection() {
  const { t } = useTranslation();
  const form = useFormContext<ContractFormValues>();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "modifications",
  });
  const [deleteIdx, setDeleteIdx] = React.useState<number | null>(null);

  const count = fields.length;
  const headerLabel = `${t(
    "engagement.contract.modifications.section.title",
  )}${
    count > 0
      ? " " +
        t("engagement.contract.modifications.count", { count })
      : ""
  }`;

  function handleAdd() {
    append(defaultModification(fields.length));
  }

  function confirmDelete() {
    if (deleteIdx !== null) remove(deleteIdx);
    setDeleteIdx(null);
  }

  return (
    <AccordionItem value="modifications">
      <AccordionTrigger
        className="text-base font-medium"
        data-testid="modifications-section-trigger"
      >
        {headerLabel}
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            {t("engagement.contract.modifications.section.helperText")}
          </p>

          {count === 0 ? (
            <p
              className="rounded-md border border-dashed py-6 text-center text-sm italic text-muted-foreground"
              data-testid="modifications-empty-state"
            >
              {t("engagement.contract.modifications.empty")}
            </p>
          ) : (
            <div className="space-y-3" data-testid="modifications-list">
              {fields.map((field, idx) => (
                <ModificationCard
                  key={field.id}
                  index={idx}
                  onDelete={() => setDeleteIdx(idx)}
                />
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAdd}
            data-testid="modifications-add-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("engagement.contract.modifications.addButton")}
          </Button>
        </div>

        <Dialog
          open={deleteIdx !== null}
          onOpenChange={(open) => {
            if (!open) setDeleteIdx(null);
          }}
        >
          <DialogContent data-testid="modifications-delete-dialog">
            <DialogHeader>
              <DialogTitle>
                {t(
                  "engagement.contract.modifications.deleteConfirm.title",
                )}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "engagement.contract.modifications.deleteConfirm.body",
                  {
                    number: deleteIdx !== null ? deleteIdx + 1 : 0,
                  },
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteIdx(null)}
                data-testid="modifications-delete-cancel"
              >
                {t(
                  "engagement.contract.modifications.deleteConfirm.cancel",
                )}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={confirmDelete}
                data-testid="modifications-delete-confirm"
              >
                {t(
                  "engagement.contract.modifications.deleteConfirm.confirm",
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </AccordionContent>
    </AccordionItem>
  );
}
